import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { CURRICULUM_TIME_UNITS } from '@/modules/curriculumsManagement'
import type { DeepCamelCase } from '@/types/utils'
import { mockQuery } from '@/utils/mocks'
import { idAndNameSchema } from '@/utils/zod'

import { createCurriculumBasketCourseMock } from '../mocks/curriculum-basket-course'

type APIResponse = z.infer<typeof responseSchema>
type CurriculumBasketCourse = DeepCamelCase<APIResponse>

export type CurriculumBasketCourseSemester =
  CurriculumBasketCourse['semesters'][number]

const responseSchema = z.object({
  course: idAndNameSchema,
  time_unit: z.enum(CURRICULUM_TIME_UNITS),
  semesters: z.array(
    z.object({
      hours: z.number().nullable(),
      is_active: z.boolean()
    })
  )
})

const parseResponse = (response: APIResponse): DeepCamelCase<APIResponse> => ({
  course: response.course,
  timeUnit: response.time_unit,
  semesters: response.semesters.map(semester => ({
    hours: semester.hours,
    isActive: semester.is_active
  }))
})

type Params = {
  curriculumId?: string
  courseId?: string
}

export const useCurriculumBasketCourse = (params: Params) =>
  useQuery({
    queryKey: ['curriculumBasketCourse', params.curriculumId, params.courseId],
    queryFn: async () => {
      if (!params.curriculumId || !params.courseId) return

      // TODO: Remove mock when API is ready - https://panowie.atlassian.net/browse/RG-367
      const response = await mockQuery(
        createCurriculumBasketCourseMock(params.curriculumId, params.courseId)
      )

      return parseResponse(responseSchema.parse(response.results))
    },
    enabled: !!params.curriculumId && !!params.courseId,
    staleTime: 1000 * 60
  })
